"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { AnimatePresence } from "framer-motion";
import Map, { Marker, MapRef, NavigationControl, AttributionControl } from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

// Enable RTL text rendering for Hebrew, Arabic, etc.
if (typeof window !== "undefined" && maplibregl.getRTLTextPluginStatus() === "unavailable") {
  maplibregl.setRTLTextPlugin(
    "https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.min.js",
    false,
  );
}
import { Settings, Moon, Sun, Mountain, Building2, Globe, HelpCircle } from "lucide-react";
import SpiderOverlay from "./SpiderOverlay";
import PanoramaMarker from "./PanoramaMarker";
import { MapStyleKey } from "./mapTheme";
import { useHelp } from "@/features/help";
import { useTheme } from "@/features/theme";

export interface SpiderFeature {
  id: string;
  baseTileUrl: string;
  previewUrl?: string;
  thumbnailUrl?: string;
  name?: string;
  width?: number;
  height?: number;
  poseHeading?: number;
  posePitch?: number;
  poseRoll?: number;
  nadirPatchUrl?: string | null;
  captureDate?: string | null;
  averageRating?: number;
  viewCount?: number;
  creatorName?: string;
  isVerifiedCreator?: boolean;
  ownerId?: string;
  defaultYaw?: number;
  defaultPitch?: number;
  defaultZoom?: number;
  introPanEnabled?: boolean;
  postcardEnabled?: boolean;
  postcardWatermark?: string | null;
  spatialAudioEnabled?: boolean;
  isEditMode?: boolean;
}

/** Imperative handle exposed to page.tsx for external navigation (sidebar links, hash routing). */
export interface MapViewerHandle {
  navigateTo: (feature: SpiderFeature & { coordinates: [number, number] }) => void;
  /** Silently reposition the map with no animation (used for deep-link pre-positioning). */
  jumpTo: (coordinates: [number, number]) => void;
  /** Fly the map to a location (e.g. from search results). */
  flyToLocation: (center: [number, number], zoom?: number) => void;
  /** Frame-accurate camera update for tour animation. duration=0 = synchronous jumpTo equivalent. */
  setCameraSmooth: (params: { center: [number, number]; zoom: number; pitch: number; bearing: number; duration: number }) => void;
  /** Fit map to a bounding box — used for the tour overview shot. */
  fitRouteBounds: (bounds: [number, number, number, number], paddingPx?: number) => void;
  /** Returns the raw MapLibre Map instance — used by TourRoutePlayer for the car marker. */
  getRawMap: () => any | null;
}

/** Screen pixel coordinates of the clicked marker — used by page.tsx to set the viewer's transform-origin. */
export interface ScreenOrigin {
  x: number;
  y: number;
}

interface MapViewerProps {
  geoJsonData: any;
  /** Called once the map has settled and the viewer should open.
   *  `origin` is the on-screen pixel position of the target marker so the
   *  caller can animate the viewer "blooming" from that exact point. */
  onSelectPanorama: (feature: SpiderFeature, origin: ScreenOrigin) => void;
  /** Fires true when the map starts moving, false when it stops. */
  onMapInteractionChange?: (active: boolean) => void;
  /** Fires immediately when the user clicks a marker, before the map flies. */
  onNavigationStart?: (feature: SpiderFeature, origin: ScreenOrigin) => void;
  /** Fires once during a long flyTo when 10 % of the zoom-in progress is reached.
   *  The caller should begin fading in the viewer at this point. */
  onNavigationProgress?: (feature: SpiderFeature) => void;
  /** ID of the feature currently being navigated to — rendered regardless of zoom/bounds. */
  pendingFeatureId?: string;
  /** Starting position — defaults to San Francisco when omitted. */
  initialCenter?: { longitude: number; latitude: number };
  /** Controlled map style — owned by the parent so overlays can react to it. */
  mapStyle?: MapStyleKey;
  /** Called when the user picks a different map style inside the settings panel. */
  onMapStyleChange?: (style: MapStyleKey) => void;
  /** Suppress the normal map-interaction UI-fade during programmatic tour animation. */
  suppressInteractionFade?: boolean;
  /** When true, clicking a marker adds it as a route waypoint instead of opening the viewer. */
  routeBuilderMode?: boolean;
  /** Called with the clicked feature when routeBuilderMode is true. */
  onRouteWaypointAdd?: (feature: any) => void;
  /** Set of panorama IDs already added as waypoints — used to show checkmark badges. */
  routeWaypointIds?: Set<string>;
  /** Ordered waypoint coordinates — used as fallback straight-line when no real geometry. */
  routeWaypoints?: Array<{ lat: number; lng: number }>;
  /** Real road-following geometry from routing API ([lng, lat] pairs, GeoJSON order). */
  routeGeometry?: [number, number][] | null;
  /** GPX track geometry ([lng, lat] pairs, GeoJSON order) rendered as a separate layer. */
  gpxRouteGeometry?: [number, number][] | null;
  /** Line color for the GPX route layer (hex string). */
  gpxRouteColor?: string;
  /** When true the GPX route is drawn as a dashed line. */
  gpxRouteDash?: boolean;
  /** Line width for the GPX route layer (pixels). */
  gpxRouteWidth?: number;
}


const SATELLITE_STYLE = {
  version: 8 as const,
  sources: {
    "esri-satellite": {
      type: "raster" as const,
      tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
      maxzoom: 19,
      attribution: "Tiles © Esri",
    },
  },
  layers: [{ id: "esri-satellite-layer", type: "raster" as const, source: "esri-satellite" }],
};

const STYLES: Record<string, string | typeof SATELLITE_STYLE> = {
  dark:      "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
  light:     "https://tiles.openfreemap.org/styles/liberty",
  satellite: SATELLITE_STYLE,
};
type StyleKey = "dark" | "light" | "satellite";

const sineEaseInOut = (t: number) => (1 - Math.cos(Math.PI * t)) / 2;

// 3D tilt used by the "3D View" toggle. Also the pitch auto-restores to once
// zooming back in past GLOBE_RESTORE_ZOOM after having been auto-flattened.
const PITCH_3D = 60;
// Boundary between "2D" and "3D" pitch — used by the toggle, the pitch-sync
// effect, and the auto globe-flatten logic to agree on what "3D is on" means.
const PITCH_3D_THRESHOLD = 30;
// Zoom band the globe curve occupies (mirrors the sky atmosphere-blend fade
// added alongside globe projection). Two thresholds give hysteresis so the
// pitch doesn't flicker back and forth while hovering right at the edge.
const GLOBE_FLATTEN_ZOOM = 5;
const GLOBE_RESTORE_ZOOM = 6;

// ── Tile preloading utilities ─────────────────────────────────────────────────

/**
 * Pre-warms tiles for the navigation animation (apex → destination).
 *
 * Requires rawMapRef.current (raw MapLibre Map), NOT the react-map-gl wrapper.
 *
 * Destination tiles are sampled first (i = N-1 → 0) so they are queued in
 * MapLibre's Worker pipeline and in the browser HTTP cache before nearby tiles.
 * Quadratic weighting concentrates samples near the destination (highest zoom)
 * where tile count is densest and prefetching has the most impact.
 *
 * Called immediately on navigation start — before zoom-out begins — so tiles
 * have the maximum possible time to arrive before the camera does.
 */
async function preloadTilesForFlyTo(
  rawMap: any,
  fromLng: number, fromLat: number,
  toLng: number,   toLat: number,
  apexZoom: number,               // pre-computed minimum zoom of the arc
  timeoutMs = 4000,
): Promise<void> {
  const transform    = rawMap.transform;
  const sourceCaches = rawMap.style?.sourceCaches as Record<string, any> | undefined;
  if (!transform || !sourceCaches) return;

  const cloned     = transform.clone();
  const tileUrlSet = new Set<string>();

  // 12 samples (up from 7), destination-first so high-zoom tiles are queued
  // in MapLibre's Worker pipeline before nearby/lower-zoom tiles.
  for (let i = 11; i >= 0; i--) {
    const t    = i / 11;                                // 1 (destination) → 0 (apex)
    const lng  = fromLng + (toLng - fromLng) * t;
    const lat  = fromLat + (toLat - fromLat) * t;
    const zoom = apexZoom + (17 - apexZoom) * t;        // apex → zoom 17

    cloned.zoom   = zoom;
    cloned.center = { lng, lat };

    for (const [, cache] of Object.entries(sourceCaches)) {
      try {
        const before: Set<string> = new Set(Object.keys(cache._tiles ?? {}));
        cache.update(cloned);

        const srcTiles: string[] = cache._source?.tiles ?? [];
        const scheme: string     = cache._source?.scheme ?? "xyz";
        if (!srcTiles.length) continue;

        for (const [key, tile] of Object.entries(cache._tiles ?? {}) as [string, any][]) {
          if (before.has(key)) continue;
          const c = tile.tileID?.canonical;
          if (!c) continue;
          const y = scheme === "tms" ? (1 << c.z) - 1 - c.y : c.y;
          tileUrlSet.add(
            srcTiles[0]
              .replace("{z}", String(c.z))
              .replace("{x}", String(c.x))
              .replace("{y}", String(y)),
          );
        }
      } catch { /* source not ready */ }
    }
  }

  if (!tileUrlSet.size) return;

  const fetches = Array.from(tileUrlSet).map(url =>
    fetch(url, { credentials: "omit" }).then(() => {}).catch(() => {}),
  );
  await Promise.race([
    Promise.allSettled(fetches),
    new Promise<void>(res => setTimeout(res, timeoutMs)),
  ]);
}

const MapViewer = forwardRef<MapViewerHandle, MapViewerProps>(function MapViewer(
  { geoJsonData, onSelectPanorama, onMapInteractionChange, onNavigationStart, onNavigationProgress, pendingFeatureId, initialCenter, mapStyle: mapStyleProp, onMapStyleChange, suppressInteractionFade, routeBuilderMode, onRouteWaypointAdd, routeWaypointIds, routeWaypoints, routeGeometry, gpxRouteGeometry, gpxRouteColor, gpxRouteDash, gpxRouteWidth },
  ref
) {
  const mapRef    = useRef<MapRef>(null);
  // Overlay controls follow the app theme; mapStyle only picks the map tiles.
  const { isDark: themeIsDark } = useTheme();
  // Raw MapLibre Map instance stored on onLoad — guaranteed to have all methods
  const rawMapRef = useRef<any>(null);
  const [spider, setSpider] = useState<{
    features: any[];
    screenX: number;
    screenY: number;
  } | null>(null);

  // ── Map settings — persisted to localStorage ─────────────────────────────
  const ls = (key: string, fb: string) =>
    typeof window !== "undefined" ? (localStorage.getItem(key) ?? fb) : fb;

  const { openHelpCenter } = useHelp();

  const [settingsOpen,        setSettingsOpen]        = useState(false);
  const [isInteracting,       setIsInteracting]       = useState(false);
  // mapStyle is controlled by the parent when mapStyleProp is provided
  const [mapStyleInternal,    setMapStyleInternal]    = useState<StyleKey>(() => ls("360map-style", "dark") as StyleKey);
  const mapStyle    = mapStyleProp    ?? mapStyleInternal;
  // Only an explicit pick is remembered — until then the map follows the app theme.
  const setMapStyle = (s: StyleKey) => {
    try { localStorage.setItem("360map-style", s); } catch {}
    setMapStyleInternal(s);
    onMapStyleChange?.(s);
  };
  const [terrainEnabled,      setTerrainEnabled]      = useState(() => ls("360map-terrain", "false") === "true");
  const [terrainExaggeration, setTerrainExaggeration] = useState(() => parseFloat(ls("360map-terrain-exag", "6")));
  const [pitch,               setPitch]               = useState(PITCH_3D);
  const [buildingsEnabled,    setBuildingsEnabled]    = useState(() => ls("360map-buildings", "false") !== "false");
  const [buildingOpacity,     setBuildingOpacity]     = useState(() => parseFloat(ls("360map-building-opacity", "0.82")));
  const [bearing,             setBearing]             = useState(0);

  // Refs for use inside stable callbacks (style.load handler)
  const buildingsEnabledRef  = useRef(buildingsEnabled);
  const buildingOpacityRef   = useRef(buildingOpacity);
  const terrainEnabledRef    = useRef(terrainEnabled);
  const terrainExagRef       = useRef(terrainExaggeration);
  // Mirrors `pitch` state for use inside onMapMove (kept stable via []) —
  // true once the user's 3D preference is "on" (pitch >= PITCH_3D_THRESHOLD)
  const pitchRef             = useRef(pitch);
  // True once the auto globe-flatten has fired, so onMapMove knows to restore
  // the 3D pitch when zooming back in past GLOBE_RESTORE_ZOOM
  const globeFlattenedRef    = useRef(false);

  // Route preview line
  const routeWaypointsRef = useRef<Array<{ lat: number; lng: number }>>([]);
  const routeGeometryRef  = useRef<[number, number][] | null>(null);
  const gpxGeometryRef    = useRef<[number, number][] | null>(null);
  const gpxColorRef       = useRef<string>("#6366f1");
  const gpxDashRef        = useRef<boolean>(false);
  const gpxWidthRef       = useRef<number>(3);

  const applyRouteLayer = useCallback((map: any) => {
    // Prefer real road geometry; fall back to straight-line between waypoints
    const coords: [number, number][] = routeGeometryRef.current
      ? routeGeometryRef.current
      : routeWaypointsRef.current.map((w) => [w.lng, w.lat] as [number, number]);

    const isRealRoute = !!routeGeometryRef.current;

    const geojson: any = {
      type: "Feature",
      geometry: { type: "LineString", coordinates: coords },
      properties: {},
    };
    if (map.getSource("route-preview")) {
      (map.getSource("route-preview") as any).setData(geojson);
      // Update visual style: solid line for real route, dashed for straight-line
      try {
        map.setPaintProperty("route-preview-dash", "line-dasharray", isRealRoute ? [1] : [4, 2.5]);
        map.setPaintProperty("route-preview-dash", "line-opacity", isRealRoute ? 0.85 : 0.9);
        map.setPaintProperty("route-preview-dash", "line-width", isRealRoute ? 3.5 : 2.5);
        map.setPaintProperty("route-preview-glow", "line-width", isRealRoute ? 12 : 9);
        map.setPaintProperty("route-preview-glow", "line-opacity", isRealRoute ? 0.22 : 0.18);
      } catch {}
    } else {
      map.addSource("route-preview", { type: "geojson", data: geojson });
      map.addLayer({
        id: "route-preview-glow",
        type: "line",
        source: "route-preview",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#6366f1", "line-width": 9, "line-opacity": 0.18 },
      });
      map.addLayer({
        id: "route-preview-dash",
        type: "line",
        source: "route-preview",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#a5b4fc", "line-width": 2.5, "line-opacity": 0.9, "line-dasharray": isRealRoute ? [1] : [4, 2.5] },
      });
    }
  }, []);

  useEffect(() => {
    routeWaypointsRef.current = routeWaypoints ?? [];
    routeGeometryRef.current  = routeGeometry ?? null;
    if (rawMapRef.current) applyRouteLayer(rawMapRef.current);
  }, [routeWaypoints, routeGeometry, applyRouteLayer]);

  const applyGpxLayer = useCallback((map: any) => {
    const coords = gpxGeometryRef.current;
    const color  = gpxColorRef.current;
    const dash   = gpxDashRef.current;
    const width  = gpxWidthRef.current;

    if (!coords || coords.length < 2) {
      // Remove layers if they exist and no data
      try {
        if (map.getLayer("gpx-preview-glow")) map.removeLayer("gpx-preview-glow");
        if (map.getLayer("gpx-preview-line")) map.removeLayer("gpx-preview-line");
        if (map.getSource("gpx-preview"))     map.removeSource("gpx-preview");
      } catch {}
      return;
    }

    const geojson: any = {
      type: "Feature",
      geometry: { type: "LineString", coordinates: coords },
      properties: {},
    };

    if (map.getSource("gpx-preview")) {
      (map.getSource("gpx-preview") as any).setData(geojson);
      try {
        map.setPaintProperty("gpx-preview-line", "line-color", color);
        map.setPaintProperty("gpx-preview-line", "line-width", width);
        map.setPaintProperty("gpx-preview-line", "line-dasharray", dash ? [4, 2.5] : [1]);
        map.setPaintProperty("gpx-preview-glow", "line-color", color);
        map.setPaintProperty("gpx-preview-glow", "line-width", width * 4);
      } catch {}
    } else {
      map.addSource("gpx-preview", { type: "geojson", data: geojson });
      map.addLayer({
        id: "gpx-preview-glow",
        type: "line",
        source: "gpx-preview",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": color, "line-width": width * 4, "line-opacity": 0.18 },
      });
      map.addLayer({
        id: "gpx-preview-line",
        type: "line",
        source: "gpx-preview",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": color,
          "line-width": width,
          "line-opacity": 0.88,
          "line-dasharray": dash ? [4, 2.5] : [1],
        },
      });
    }
  }, []);

  useEffect(() => {
    gpxGeometryRef.current = gpxRouteGeometry ?? null;
    gpxColorRef.current    = gpxRouteColor ?? "#6366f1";
    gpxDashRef.current     = gpxRouteDash ?? false;
    gpxWidthRef.current    = gpxRouteWidth ?? 3;
    if (rawMapRef.current) applyGpxLayer(rawMapRef.current);
  }, [gpxRouteGeometry, gpxRouteColor, gpxRouteDash, gpxRouteWidth, applyGpxLayer]);

  useEffect(() => {
    pitchRef.current = pitch;
    localStorage.setItem("360map-pitch", String(pitch));
    // 3D turned off — the globe-flatten auto-transition has nothing to restore to
    if (pitch < PITCH_3D_THRESHOLD) globeFlattenedRef.current = false;
    const map = rawMapRef.current;
    if (!map || Math.abs(map.getPitch() - pitch) < 0.1) return;
    map.easeTo({ pitch, duration: 400 });
  }, [pitch]);

  useEffect(() => {
    buildingsEnabledRef.current = buildingsEnabled;
    buildingOpacityRef.current  = buildingOpacity;
    localStorage.setItem("360map-buildings", String(buildingsEnabled));
    localStorage.setItem("360map-building-opacity", String(buildingOpacity));
    const map = rawMapRef.current;
    if (!map) return;
    if (map.getLayer("3d-buildings")) {
      map.setLayoutProperty("3d-buildings", "visibility", buildingsEnabled ? "visible" : "none");
      map.setPaintProperty("3d-buildings", "fill-extrusion-opacity", buildingOpacity);
    }
  }, [buildingsEnabled, buildingOpacity]);

  useEffect(() => {
    terrainEnabledRef.current = terrainEnabled;
    terrainExagRef.current    = terrainExaggeration;
    localStorage.setItem("360map-terrain", String(terrainEnabled));
    localStorage.setItem("360map-terrain-exag", String(terrainExaggeration));
    const map = rawMapRef.current;
    if (!map) return;
    map.setTerrain(terrainEnabled ? { source: "terrain-dem", exaggeration: terrainExaggeration } : null);
  }, [terrainEnabled, terrainExaggeration]);

  useEffect(() => {
    localStorage.setItem("360map-bearing", String(bearing));
    const map = rawMapRef.current;
    if (!map || Math.abs(map.getBearing() - bearing) < 0.1) return;
    map.easeTo({ bearing, duration: 400 });
  }, [bearing]);

  // ── Terrain source + 3D buildings — called on load and after every style change ──
  const setupLayersAndTerrain = useCallback((map: any) => {
    // Globe projection — MapLibre auto-transitions globe ↔ flat mercator as the user zooms
    try {
      map.setProjection({ type: "globe" });
      // Atmosphere glow around the globe, fading out as the map flattens
      map.setSky({
        "atmosphere-blend": ["interpolate", ["linear"], ["zoom"], 0, 1, 5, 1, 7, 0],
      });
      map.setLight({ anchor: "map", position: [1.5, 90, 80] });
    } catch (err) {
      console.warn("Globe projection unavailable:", err);
    }
    // Terrain DEM source (free AWS/Mapzen Terrarium tiles, no API key needed)
    if (!map.getSource("terrain-dem")) {
      map.addSource("terrain-dem", {
        type: "raster-dem",
        tiles: ["https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png"],
        encoding: "terrarium",
        tileSize: 256,
      });
    }
    // 3D building extrusions
    try {
      if (!map.getLayer("3d-buildings")) {
        const sources = Object.keys(map.getStyle()?.sources ?? {});
        const sourceName = sources.find((s) => s !== "terrain-dem" && (map.getSource(s) as any)?.type === "vector");
        if (sourceName) {
          map.addLayer({
            id: "3d-buildings",
            type: "fill-extrusion",
            source: sourceName,
            "source-layer": "building",
            minzoom: 13,
            paint: {
              "fill-extrusion-color": "#1e293b",
              "fill-extrusion-height": ["coalesce", ["get", "render_height"], ["*", ["get", "levels"], 3], 8],
              "fill-extrusion-base":   ["coalesce", ["get", "render_min_height"], 0],
              "fill-extrusion-opacity": buildingOpacityRef.current,
            },
          });
        }
      }
      if (map.getLayer("3d-buildings")) {
        map.setLayoutProperty("3d-buildings", "visibility", buildingsEnabledRef.current ? "visible" : "none");
        map.setPaintProperty("3d-buildings", "fill-extrusion-opacity", buildingOpacityRef.current);
      }
    } catch (err) {
      console.warn("3D buildings layer unavailable:", err);
    }
    // Re-apply terrain after style reload
    try {
      map.setTerrain(terrainEnabledRef.current
        ? { source: "terrain-dem", exaggeration: terrainExagRef.current }
        : null);
    } catch {}
    // Force English labels — prefer name_en, fall back to local name
    try {
      (map.getStyle()?.layers ?? []).forEach((layer: any) => {
        if (layer.type === "symbol" && layer.layout?.["text-field"]) {
          map.setLayoutProperty(layer.id, "text-field", [
            "coalesce", ["get", "name_en"], ["get", "name"],
          ]);
        }
      });
    } catch {}
  }, []);

  // ── React marker state ────────────────────────────────────────────────────
  const [currentZoom, setCurrentZoom] = useState(12);
  const [isNavigating, setIsNavigating] = useState(false);
  const [mapBounds, setMapBounds] = useState<{ minLon: number; maxLon: number; minLat: number; maxLat: number } | null>(null);
  const zoomRafRef = useRef<number>(0);
  const moveEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMapReadyRef = useRef(false);
  // Mirrors isNavigating / suppressInteractionFade for onMapMove (kept stable via [])
  const isNavigatingRef = useRef(isNavigating);
  const suppressInteractionFadeRef = useRef(suppressInteractionFade);
  useEffect(() => { isNavigatingRef.current = isNavigating; }, [isNavigating]);
  useEffect(() => { suppressInteractionFadeRef.current = suppressInteractionFade; }, [suppressInteractionFade]);

  /**
   * Drives a pitch-only transition by hand via rAF + map.setPitch(), instead of
   * map.easeTo({ pitch }). MapLibre keeps a single shared camera animation —
   * a concurrent easeTo (e.g. the one the mouse-wheel/scroll-zoom handler fires
   * on every tick) cancels and replaces whatever easeTo is already in flight,
   * so a pitch-only easeTo gets killed almost immediately by continuous
   * scrolling and never reaches its target. setPitch() is a direct transform
   * mutation outside that shared animation, so it survives concurrent zooming.
   */
  const pitchAnimGenRef = useRef(0);
  const animatePitchTo = useCallback((target: number, duration = 900) => {
    const map = rawMapRef.current;
    if (!map) return;
    const gen = ++pitchAnimGenRef.current;
    const startPitch = map.getPitch();
    const startTime = performance.now();
    const step = (now: number) => {
      if (pitchAnimGenRef.current !== gen || !rawMapRef.current) return; // superseded/unmounted
      const t = Math.min(1, (now - startTime) / duration);
      rawMapRef.current.setPitch(startPitch + (target - startPitch) * sineEaseInOut(t));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  // Refs so callbacks never need to appear in useCallback dep arrays
  const onNavigationStartRef    = useRef(onNavigationStart);
  const onNavigationProgressRef = useRef(onNavigationProgress);
  useEffect(() => { onNavigationStartRef.current    = onNavigationStart;    }, [onNavigationStart]);
  useEffect(() => { onNavigationProgressRef.current = onNavigationProgress; }, [onNavigationProgress]);

  /**
   * Tracks an in-flight flyTo for zoom-progress-based early viewer open.
   * apexZoom starts at Infinity and drops to the minimum zoom seen during the
   * parabolic arc; once zoom rises past apexZoom we're in the zoom-in phase
   * and we fire onNavigationProgress at 70 % of that phase.
   */
  const navRef = useRef<{
    feature: SpiderFeature;
    targetZoom: number;
    apexZoom: number;
    progressFired: boolean;
  } | null>(null);

  /**
   * Bounds-only handler — updates mapBounds during move/zoom without touching
   * currentZoom state (which caused an infinite render loop when called from
   * react-map-gl's onZoom/onMove callbacks during reconciliation).
   * currentZoom is updated on moveend instead.
   */
  const onMapMove = useCallback(() => {
    cancelAnimationFrame(zoomRafRef.current);
    zoomRafRef.current = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map) return;
      const zoom = map.getZoom();

      if (zoom >= 2) {
        const b = map.getBounds();
        if (b) setMapBounds({ minLon: b.getWest(), maxLon: b.getEast(), minLat: b.getSouth(), maxLat: b.getNorth() });
      } else {
        setMapBounds(null);
      }

      // Auto-flatten to top-down while the globe curve is visible (3D pitch
      // would otherwise push the globe toward the bottom of the viewport),
      // then restore the user's 3D pitch once zoomed back into flat mode.
      // Only engages when the "3D View" toggle is on, and stays out of the
      // way of tour playback / click-to-navigate flyTo animations.
      const rawMap = rawMapRef.current;
      if (rawMap && isMapReadyRef.current && !suppressInteractionFadeRef.current && !isNavigatingRef.current) {
        if (pitchRef.current >= PITCH_3D_THRESHOLD) {
          if (zoom < GLOBE_FLATTEN_ZOOM && !globeFlattenedRef.current) {
            globeFlattenedRef.current = true;
            animatePitchTo(0);
          } else if (zoom > GLOBE_RESTORE_ZOOM && globeFlattenedRef.current) {
            globeFlattenedRef.current = false;
            animatePitchTo(PITCH_3D);
          }
        }
      }
    });
  }, []);

  useEffect(() => () => {
    cancelAnimationFrame(zoomRafRef.current);
    if (moveEndTimerRef.current) clearTimeout(moveEndTimerRef.current);
  }, []);

  // When tour ends (suppressInteractionFade flips false), clear any stuck
  // interacting state so the UI overlay returns to full opacity immediately.
  const prevSuppressRef = useRef(suppressInteractionFade);
  useEffect(() => {
    if (prevSuppressRef.current && !suppressInteractionFade) {
      if (moveEndTimerRef.current) { clearTimeout(moveEndTimerRef.current); moveEndTimerRef.current = null; }
      setIsInteracting(false);
      onMapInteractionChange?.(false);
    }
    prevSuppressRef.current = suppressInteractionFade;
  }, [suppressInteractionFade, onMapInteractionChange]);

  /**
   * One React marker per spatial group, visible from zoom 2+ (≈90 % max zoom-out).
   * Photo card appears at zoom 10+ (≈45 %); below that it crossfades to the teardrop pin.
   *
   * Grouping precision scales with zoom so nearby photos naturally merge at
   * lower zoom levels and separate as the user zooms in:
   *   z2–3:   ~110 km  (toFixed 0)
   *   z4–5:   ~11 km   (toFixed 1)
   *   z6–7:   ~1 km    (toFixed 2)
   *   z8–14:  ~110 m   (toFixed 3)
   *   z15+:   ~11 m    (toFixed 4)
   *
   * Each group shows the most-recently-uploaded thumbnail and a count badge.
   */
  const reactMarkers = useMemo(() => {
    const features: any[] = geoJsonData?.features ?? [];

    // During navigation: show ONLY the pending feature as a solo marker regardless
    // of zoom or bounds, and suppress all other markers. This keeps the destination
    // pin visible even at maximum zoom-out while the map flies.
    if (isNavigating && pendingFeatureId) {
      const pf = features.find((f) => f.properties?.id === pendingFeatureId);
      return pf ? [{ feature: pf, count: 1, siblings: [pf] }] : [];
    }

    if (!mapBounds || currentZoom < 2) {
      // Not navigating and map not ready — still surface the pending feature if set
      if (pendingFeatureId) {
        const pf = features.find((f) => f.properties?.id === pendingFeatureId);
        return pf ? [{ feature: pf, count: 1, siblings: [pf] }] : [];
      }
      return [];
    }

    // Pixel-radius greedy clustering:
    // Compute how many degrees one pixel spans at this zoom level, then derive a
    // cluster radius equal to the rendered marker width so that any two markers
    // whose thumbnails would visually overlap end up in the same cluster.
    const degPerPx = 360 / (256 * Math.pow(2, currentZoom));
    // Mirror the size logic in PanoramaMarker: icon=20px at z<7, grows to 60px at z16+
    const markerPx =
      currentZoom < 7  ? 20 :
      currentZoom < 10 ? 30 :
      36 + Math.min(1, (currentZoom - 10) / 6) * 24;
    const CLUSTER_R = markerPx * degPerPx;

    const buf = CLUSTER_R;
    const inBounds: any[] = [];

    for (const f of features) {
      const [lon, lat] = f.geometry?.coordinates ?? [];
      if (lon == null) continue;
      if (lon < mapBounds.minLon - buf || lon > mapBounds.maxLon + buf) continue;
      if (lat < mapBounds.minLat - buf || lat > mapBounds.maxLat + buf) continue;
      inBounds.push(f);
    }

    // Sort by longitude for deterministic, scan-line-friendly grouping
    inBounds.sort((a, b) => (a.geometry.coordinates[0] ?? 0) - (b.geometry.coordinates[0] ?? 0));

    type Cluster = { feature: any; count: number; siblings: any[]; cLon: number; cLat: number };
    const clusters: Cluster[] = [];

    for (const f of inBounds) {
      const [lon, lat] = f.geometry.coordinates;
      const fDate = f.properties?.created_at ?? "";

      // Find the nearest existing cluster within CLUSTER_R (Chebyshev distance)
      let nearest: Cluster | null = null;
      let nearestDist = Infinity;
      for (const c of clusters) {
        const d = Math.max(Math.abs(lon - c.cLon), Math.abs(lat - c.cLat));
        if (d < CLUSTER_R && d < nearestDist) {
          nearest = c;
          nearestDist = d;
        }
      }

      if (!nearest) {
        clusters.push({ feature: f, count: 1, siblings: [f], cLon: lon, cLat: lat });
      } else {
        nearest.count++;
        nearest.siblings.push(f);
        // Update centroid (running mean)
        nearest.cLon = (nearest.cLon * (nearest.count - 1) + lon) / nearest.count;
        nearest.cLat = (nearest.cLat * (nearest.count - 1) + lat) / nearest.count;
        // Keep most-recently-uploaded thumbnail on top
        if (fDate > (nearest.feature.properties?.created_at ?? "")) {
          nearest.feature = f;
        }
      }
    }

    const results: { feature: any; count: number; siblings: any[] }[] = clusters;

    // Always keep the pending feature visible even if it fell outside bounds
    if (pendingFeatureId) {
      const has = results.some((r) => r.siblings.some((f) => f.properties?.id === pendingFeatureId));
      if (!has) {
        const pf = features.find((f) => f.properties?.id === pendingFeatureId);
        if (pf) results.push({ feature: pf, count: 1, siblings: [pf] });
      }
    }

    return results;
  }, [geoJsonData, mapBounds, currentZoom, isNavigating, pendingFeatureId]);

  /**
   * Unified navigation entry-point.
   *
   * Behaviour:
   *  - Nearby/same-zoom → smooth easeTo (700 ms)
   *  - Far away or mismatched zoom → flyTo with MapLibre's parabolic arc:
   *    automatically zooms out to a bird's-eye view, travels, then descends.
   *
   * After `moveend` the projected screen position of the marker is forwarded to
   * `onSelectPanorama` as `ScreenOrigin` so page.tsx can bloom the viewer from
   * exactly that point — no coloured overlay needed.
   */
  const performNavigation = useCallback(
    (feature: SpiderFeature, coordinates: [number, number]) => {
      const map = mapRef.current;
      if (!map) {
        // Map style not loaded yet (e.g. direct URL open in a new tab).
        // Skip the fly-to and open the panorama immediately from screen centre.
        const cx = typeof window !== "undefined" ? window.innerWidth / 2 : 400;
        const cy = typeof window !== "undefined" ? window.innerHeight / 2 : 300;
        onNavigationStartRef.current?.(feature, { x: cx, y: cy });
        onSelectPanorama(feature, { x: cx, y: cy });
        return;
      }

      setSpider(null);

      const [lon, lat] = coordinates;

      // Fire immediately so the bubble / pending-marker logic starts before the map moves
      const pt = map.project([lon, lat] as [number, number]);
      const clickOrigin: ScreenOrigin = pt
        ? { x: pt.x, y: pt.y }
        : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      onNavigationStartRef.current?.(feature, clickOrigin);
      setIsNavigating(true);

      // Preload thumbnail so it's in browser cache when the marker becomes visible
      if (feature.thumbnailUrl) {
        const img = new window.Image();
        img.src = feature.thumbnailUrl;
      }
      const currentCenter = map.getCenter();
      const dx = lon - currentCenter.lng;
      const dy = lat - currentCenter.lat;
      const geoDistance = Math.sqrt(dx * dx + dy * dy);
      const currentZoom = map.getZoom();

      const needsFly = geoDistance > 0.008 || Math.abs(currentZoom - 17) > 1.5;

      if (needsFly) {
        // ── Compute arc geometry (needed for tile preloading) ────────────────────
        const apexDip    = Math.log2(Math.max(1, geoDistance / 0.0005)) * 0.8;
        const apexZoom   = Math.max(2, Math.min(currentZoom, 17) - apexDip);
        const zoomInDepth = 17 - apexZoom;

        // ── Start tile preloading immediately (fire-and-forget) ──────────────────
        // Fires before flyTo begins so tiles have the entire animation in-flight.
        preloadTilesForFlyTo(rawMapRef.current, currentCenter.lng, currentCenter.lat, lon, lat, apexZoom);

        // ── Zoom-progress listener: opens viewer at 90 % of zoom-in range ────────
        // With sine ease-in-out this fires at ≈85 % of total duration — late enough
        // that the camera is visibly decelerating, early enough for a clean fade-in.
        navRef.current = { feature, targetZoom: 17, apexZoom: Infinity, progressFired: false };
        const onZoomDuringNav = () => {
          const nav = navRef.current;
          if (!nav || nav.progressFired) { map.off("zoom", onZoomDuringNav); return; }
          const z = map.getZoom();
          if (z < nav.apexZoom) nav.apexZoom = z;
          const zoomRange = nav.targetZoom - nav.apexZoom;
          if (zoomRange > 0.5 && z > nav.apexZoom + 0.5) {
            const progress = (z - nav.apexZoom) / zoomRange;
            if (progress >= 0.9) {
              nav.progressFired = true;
              map.off("zoom", onZoomDuringNav);
              onNavigationProgressRef.current?.(nav.feature);
            }
          }
        };
        map.on("zoom", onZoomDuringNav);

        // ── Single flyTo — sine ease-in-out across the full parabolic arc ────────
        // Eliminates the two-phase easeTo seam that caused premature viewer opening.
        // Velocity profile: slow (0–20 %) → fast (20–70 %) → slow (70–90 %) → done.
        const duration = Math.min(6000, Math.max(4200, geoDistance * 1500 + zoomInDepth * 100 + 3000));

        map.once("moveend", () => {
          const openedEarly = navRef.current?.progressFired ?? false;
          navRef.current = null;
          setIsNavigating(false);
          if (openedEarly) return;
          setTimeout(() => {
            const point = map.project([lon, lat]);
            if (!point) return;
            onSelectPanorama(feature, { x: point.x, y: point.y });
          }, 50);
        });

        map.flyTo({
          center:   [lon, lat],
          zoom:     17,
          duration,
          easing:   sineEaseInOut,
        });

      } else {
        map.once("moveend", () => {
          navRef.current = null;
          setIsNavigating(false);
          setTimeout(() => {
            const point = map.project([lon, lat]);
            if (!point) return;
            onSelectPanorama(feature, { x: point.x, y: point.y });
          }, 50);
        });
        map.easeTo({ center: [lon, lat], zoom: 17, duration: 380 });
      }
    },
    [onSelectPanorama]
  );


  useImperativeHandle(
    ref,
    () => ({
      navigateTo: ({ coordinates, ...feature }) => {
        performNavigation(feature, coordinates);
      },
      jumpTo: ([lon, lat]) => {
        mapRef.current?.jumpTo({ center: [lon, lat], zoom: 17 });
      },
      flyToLocation: (center, zoom = 12) => {
        mapRef.current?.flyTo({ center, zoom, duration: 1200 });
      },
      setCameraSmooth: ({ center, zoom, pitch, bearing, duration }) => {
        if (!center || center[0] == null || !isFinite(center[0]) || !isFinite(center[1])) return;
        rawMapRef.current?.easeTo({ center, zoom, pitch, bearing, duration });
      },
      fitRouteBounds: (bounds, paddingPx = 80) => {
        rawMapRef.current?.fitBounds(
          [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
          { padding: paddingPx, duration: 1800, pitch: 30, bearing: 0,
            easing: sineEaseInOut },
        );
      },
      getRawMap: () => rawMapRef.current,
    }),
    [performNavigation]
  );

  // Clicking the map background closes any open spider
  const onMapClick = useCallback(() => setSpider(null), []);

  const handleSpiderSelect = useCallback(
    (f: any) => {
      setSpider(null);
      if (routeBuilderMode && onRouteWaypointAdd) {
        onRouteWaypointAdd(f);
        return;
      }
      const p = f.properties;
      performNavigation(
        {
          id: p.id,
          baseTileUrl: p.base_tile_url || "",
          previewUrl: p.preview_url,
          thumbnailUrl: p.thumbnail_url,
          name: p.name,
          width: p.width,
          height: p.height,
          poseHeading: p.pose_heading,
          posePitch: p.pose_pitch,
          poseRoll: p.pose_roll,
          nadirPatchUrl: p.nadir_patch_url,
          captureDate: p.capture_date ?? null,
          averageRating: p.average_rating,
          viewCount: p.view_count,
          creatorName: p.creator_name,
          isVerifiedCreator: p.is_verified_creator,
          ownerId: p.user_id,
          defaultYaw: p.default_yaw,
          defaultPitch: p.default_pitch,
          defaultZoom: p.default_zoom,
          introPanEnabled: p.intro_pan_enabled ?? true,
          postcardEnabled: p.postcard_enabled ?? true,
          postcardWatermark: p.postcard_watermark ?? null,
          spatialAudioEnabled: p.spatial_audio_enabled ?? true,
        },
        f.geometry.coordinates
      );
    },
    [performNavigation, routeBuilderMode, onRouteWaypointAdd]
  );

  return (
    <div className="absolute inset-0 z-0">
      <div className="map-starfield absolute inset-0" aria-hidden="true" />
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: initialCenter?.longitude ?? -122.4194,
          latitude:  initialCenter?.latitude  ?? 37.7749,
          zoom: 11,
          pitch: pitch,
          bearing: bearing,
        }}
        mapStyle={STYLES[mapStyle]}
        onClick={onMapClick}
        onLoad={(e) => {
          const map = e.target;
          rawMapRef.current = map; // store raw MapLibre instance for effects
          const zoom = map.getZoom();
          setCurrentZoom(zoom);
          if (zoom >= 2) {
            const b = map.getBounds();
            if (b) setMapBounds({ minLon: b.getWest(), maxLon: b.getEast(), minLat: b.getSouth(), maxLat: b.getNorth() });
          }
          // Initial terrain source + 3D buildings setup
          setupLayersAndTerrain(map);
          // Re-apply after any future style change (dark ↔ light switch)
          map.on("style.load", () => { setupLayersAndTerrain(map); applyRouteLayer(map); applyGpxLayer(map); });
          // Initial route preview line
          applyRouteLayer(map);
          applyGpxLayer(map);

          // Set ready flag after a short delay to ignore initialization camera noise
          setTimeout(() => { isMapReadyRef.current = true; }, 100);

          // Start attribution collapsed — MapLibre adds maplibregl-compact-show by default
          // even with compact:true, so remove it to begin in the closed button state.
          const attrib = map.getContainer().querySelector(".maplibregl-ctrl-attrib");
          if (attrib) attrib.classList.remove("maplibregl-compact-show");
        }}
        onZoom={onMapMove}
        onMove={onMapMove}
        onMoveStart={() => {
          if (!isMapReadyRef.current || suppressInteractionFade) return;
          if (moveEndTimerRef.current) clearTimeout(moveEndTimerRef.current);
          setIsInteracting(true);
          onMapInteractionChange?.(true);
        }}
        onMoveEnd={() => {
          // Update zoom state here (not during move) to avoid render-loop.
          // This must keep running during tour playback too: currentZoom drives
          // marker clustering and the pin/photo crossfade, and when it was frozen
          // for the whole tour, stops within a couple of km of each other stayed
          // merged into one cluster marker even after zooming in to street level
          // — so arriving at a photo showed no icon for it. Quantized (0.05) so
          // the per-frame programmatic camera moves don't cause a re-render each.
          const map = mapRef.current;
          if (map) {
            const z = map.getZoom();
            setCurrentZoom((prev) => (Math.abs(prev - z) < 0.05 ? prev : z));
          }
          // The interaction-fade timer is only for user-driven moves.
          if (suppressInteractionFade) return;
          moveEndTimerRef.current = setTimeout(() => {
            setIsInteracting(false);
            onMapInteractionChange?.(false);
          }, 500);
        }}
        cursor="crosshair"
        attributionControl={false}
      >
        {/* Attribution — compact (collapsed) by default, expands on click */}
        <AttributionControl compact={true} position="bottom-right" />

        {/* Navigation controls — bottom-right, respects iOS safe-area inset */}
        <NavigationControl
          position="bottom-right"
          style={{ marginBottom: "calc(var(--sai-bottom, 0px) + 16px)", marginRight: 16 }}
        />

        {/* Thumbnail markers — visible from zoom 10, grouped by proximity */}
        <AnimatePresence mode="sync">
        {reactMarkers.map(({ feature: f, count, siblings }) => {
          const [lon, lat] = f.geometry.coordinates;
          const p = f.properties;

          return (
            <Marker
              key={`${lon.toFixed(5)},${lat.toFixed(5)}-${p.id}`}
              longitude={lon}
              latitude={lat}
              anchor="bottom"
              style={{ zIndex: 10 }}
            >
              <PanoramaMarker
                thumbnailUrl={p.thumbnail_url}
                zoom={currentZoom}
                count={count}
                name={p.name ?? undefined}
                mapStyle={mapStyle}
                featureId={p.id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (count > 1) {
                    const lons = siblings.map((f: any) => f.geometry.coordinates[0] as number);
                    const lats = siblings.map((f: any) => f.geometry.coordinates[1] as number);
                    const minLon = Math.min(...lons), maxLon = Math.max(...lons);
                    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
                    const spread = Math.max(maxLon - minLon, maxLat - minLat);
                    // Photos within ~22 m or already at max zoom → show spider picker
                    if (spread < 0.0002 || currentZoom >= 16) {
                      const point = mapRef.current?.project([lon, lat]);
                      if (point) setSpider({ features: siblings, screenX: point.x, screenY: point.y });
                    } else {
                      // Zoom to fit the cluster so photos start to separate
                      rawMapRef.current?.fitBounds(
                        [[minLon, minLat], [maxLon, maxLat]] as [[number, number], [number, number]],
                        { padding: 80, duration: 700, maxZoom: 16 }
                      );
                    }
                    return;
                  }
                  if (routeBuilderMode && onRouteWaypointAdd) {
                    onRouteWaypointAdd(f);
                    return;
                  }
                  performNavigation(
                    {
                      id: p.id,
                      baseTileUrl: p.base_tile_url || "",
                      previewUrl: p.preview_url,
                      thumbnailUrl: p.thumbnail_url,
                      name: p.name,
                      width: p.width,
                      height: p.height,
                      poseHeading: p.pose_heading,
                      posePitch: p.pose_pitch,
                      poseRoll: p.pose_roll,
                      nadirPatchUrl: p.nadir_patch_url,
                      captureDate: p.capture_date ?? null,
                      creatorName: p.creator_name,
                      isVerifiedCreator: p.is_verified_creator,
                      ownerId: p.user_id,
                      defaultYaw: p.default_yaw,
                      defaultPitch: p.default_pitch,
                      defaultZoom: p.default_zoom,
                      introPanEnabled: p.intro_pan_enabled ?? true,
                      postcardEnabled: p.postcard_enabled ?? true,
                      postcardWatermark: p.postcard_watermark ?? null,
                      spatialAudioEnabled: p.spatial_audio_enabled ?? true,
                    },
                    [lon, lat]
                  );
                }}
              />
            </Marker>
          );
        })}
        </AnimatePresence>
      </Map>

      {/* Spider Overlay */}
      {spider && (
        <SpiderOverlay
          features={spider.features.map((f) => ({
            id: f.properties.id,
            baseTileUrl: f.properties.base_tile_url || "",
            previewUrl: f.properties.preview_url,
            thumbnailUrl: f.properties.thumbnail_url,
            name: f.properties.name,
            width: f.properties.width,
            height: f.properties.height,
            poseHeading: f.properties.pose_heading,
            posePitch: f.properties.pose_pitch,
            poseRoll: f.properties.pose_roll,
            nadirPatchUrl: f.properties.nadir_patch_url,
            captureDate: f.properties.capture_date,
            averageRating: f.properties.average_rating,
            creatorName: f.properties.creator_name,
            defaultYaw: f.properties.default_yaw,
          defaultPitch: f.properties.default_pitch,
          defaultZoom: f.properties.default_zoom,
        }))}
        screenX={spider.screenX}
        screenY={spider.screenY}
        mapStyle={mapStyle}
        onSelect={(sf) => {
          const original = spider.features.find((f) => f.properties.id === sf.id);
          if (original) handleSpiderSelect(original);
        }}
        onClose={() => setSpider(null)}
      />
    )}

      {/* ── Map Settings Panel + Help button ────────────────────────────────── */}
      <div
        className="absolute z-10 flex flex-col gap-2"
        style={{
          bottom: "calc(var(--sai-bottom, 0px) + 166px)",
          right: 16,
          transition: `opacity ${isInteracting ? "180ms" : "350ms"} ease-out`,
          opacity: isInteracting ? 0.2 : 1,
          pointerEvents: isInteracting ? "none" : undefined,
        }}
      >
        {/* Gear trigger button */}
        {(() => {
          const isDark = themeIsDark;
          return (
            <>
              <button
                onClick={() => setSettingsOpen((v) => !v)}
                title="Map settings"
                className={`w-9 h-9 flex items-center justify-center rounded-[10px] border-none cursor-pointer transition-all duration-200 ${
                  settingsOpen
                    ? "bg-blue-600 shadow-[0_4px_14px_rgba(37,99,235,0.4)]"
                    : isDark
                      ? "bg-[#374151] hover:bg-[#4b5563]"
                      : "bg-white/60 hover:bg-white/75 border border-white/80 shadow-sm"
                }`}
              >
                <Settings className={`w-4 h-4 ${settingsOpen ? "text-ink" :"text-ink"}`} />
              </button>

              {/* Help button */}
              <button
                onClick={() => openHelpCenter()}
                title="Help"
                className={`w-9 h-9 flex items-center justify-center rounded-[10px] border-none cursor-pointer transition-all duration-200 ${
                  isDark
                    ? "bg-[#374151] hover:bg-[#4b5563]"
                    : "bg-white/60 hover:bg-white/75 border border-white/80 shadow-sm"
                }`}
              >
                <HelpCircle className={`w-4 h-4 text-tone-300`} />
              </button>

              {/* Settings panel — opens upward */}
              {settingsOpen && (
                <div className={`absolute bottom-full mb-2 right-0 w-44 backdrop-blur-2xl rounded-xl p-2 shadow-2xl flex flex-col gap-1.5 ${
                  isDark ? "bg-black/75 border border-white/10" : "bg-white/60 border border-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.07)]"
                }`}>
                  <p className={`text-[8px] font-black uppercase tracking-[0.2em] px-1 text-tone-500`}>Map Settings</p>

                  {/* ── Map Style ── */}
                  <div className="flex flex-col gap-1">
                    <span className={`text-[9px] font-semibold px-1 text-tone-400`}>Style</span>
                    <div className="flex gap-1">
                      {(["dark", "light"] as StyleKey[]).map((s) => (
                        <button key={s} onClick={() => setMapStyle(s)}
                          className={`flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[9px] font-bold transition-all ${
                            mapStyle === s
                              ? "bg-blue-600 text-white shadow-[0_4px_14px_rgba(37,99,235,0.4)]"
                              :"bg-ink/5 text-tone-400 hover:text-ink hover:bg-ink/10"
                          }`}
                        >
                          {s === "dark" ? <Moon className="w-2.5 h-2.5" /> : <Sun className="w-2.5 h-2.5" />}
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setMapStyle("satellite")}
                      className={`w-full flex items-center justify-center gap-1 py-1 rounded-lg text-[9px] font-bold transition-all ${
                        mapStyle === "satellite"
                          ? "bg-blue-600 text-white shadow-[0_4px_14px_rgba(37,99,235,0.4)]"
                          :"bg-ink/5 text-tone-400 hover:text-ink hover:bg-ink/10"
                      }`}
                    >
                      <Globe className="w-2.5 h-2.5" /> Satellite
                    </button>
                  </div>

                  <div className={`h-px bg-ink/10`} />

                  {/* ── 3D View ── */}
                  <button onClick={() => setPitch(v => v >= PITCH_3D_THRESHOLD ? 0 : PITCH_3D)}
                    className={`flex items-center justify-between px-1 py-0.5 rounded-lg transition-all hover:bg-ink/5`}
                  >
                    <span className={`flex items-center gap-1.5 text-[10px] text-tone-300`}>
                      <span className={`text-[9px] font-black w-3 text-center text-tone-400`}>3D</span> 3D View
                    </span>
                    <span className={`relative shrink-0 w-8 h-4 rounded-full transition-colors duration-200 ${pitch >= PITCH_3D_THRESHOLD ? "bg-blue-600" :"bg-tone-600"}`}>
                      <span className={`absolute left-0.5 top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform duration-200 ${pitch >= PITCH_3D_THRESHOLD ? "translate-x-4" : "translate-x-0"}`} />
                    </span>
                  </button>

                  <div className={`h-px bg-ink/10`} />

                  {/* ── Terrain ── */}
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setTerrainEnabled((v) => !v)}
                      className={`flex items-center justify-between px-1 py-0.5 rounded-lg transition-all hover:bg-ink/5`}
                    >
                      <span className={`flex items-center gap-1.5 text-[10px] text-tone-300`}>
                        <Mountain className={`w-3 h-3 text-tone-400`} /> Terrain
                      </span>
                      <span className={`relative shrink-0 w-8 h-4 rounded-full transition-colors duration-200 ${terrainEnabled ? "bg-blue-600" :"bg-tone-600"}`}>
                        <span className={`absolute left-0.5 top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform duration-200 ${terrainEnabled ? "translate-x-4" : "translate-x-0"}`} />
                      </span>
                    </button>

                    {terrainEnabled && (
                      <div className="flex flex-col gap-1 px-1">
                        <div className="flex justify-between items-center">
                          <span className={`text-[9px] font-semibold text-tone-400`}>Exaggeration</span>
                          <span className={`text-[9px] tabular-nums text-tone-500`}>{terrainExaggeration.toFixed(1)}×</span>
                        </div>
                        <input type="range" min={0} max={20} step={0.5} value={terrainExaggeration}
                          onChange={(e) => setTerrainExaggeration(parseFloat(e.target.value))}
                          className={`w-full h-1 rounded-full appearance-none accent-blue-500 cursor-pointer bg-ink/10`}
                        />
                      </div>
                    )}
                  </div>

                  <div className={`h-px bg-ink/10`} />

                  {/* ── 3D Buildings ── */}
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setBuildingsEnabled((v) => !v)}
                      className={`flex items-center justify-between px-1 py-0.5 rounded-lg transition-all hover:bg-ink/5`}
                    >
                      <span className={`flex items-center gap-1.5 text-[10px] text-tone-300`}>
                        <Building2 className={`w-3 h-3 text-tone-400`} /> 3D Buildings
                      </span>
                      <span className={`relative shrink-0 w-8 h-4 rounded-full transition-colors duration-200 ${buildingsEnabled ? "bg-blue-600" :"bg-tone-600"}`}>
                        <span className={`absolute left-0.5 top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform duration-200 ${buildingsEnabled ? "translate-x-4" : "translate-x-0"}`} />
                      </span>
                    </button>

                    {buildingsEnabled && (
                      <div className="flex flex-col gap-1 px-1">
                        <div className="flex justify-between items-center">
                          <span className={`text-[9px] font-semibold text-tone-400`}>Opacity</span>
                          <span className={`text-[9px] tabular-nums text-tone-500`}>{Math.round(buildingOpacity * 100)}%</span>
                        </div>
                        <input type="range" min={0} max={1} step={0.05} value={buildingOpacity}
                          onChange={(e) => setBuildingOpacity(parseFloat(e.target.value))}
                          className={`w-full h-1 rounded-full appearance-none accent-blue-500 cursor-pointer bg-ink/10`}
                        />
                      </div>
                    )}
                  </div>

                </div>
              )}
            </>
          );
        })()}
      </div>
  </div>
);
});

export default MapViewer;
